import { useState, useEffect, useCallback } from 'react';
import { HandCoins, ArrowDownToLine, ArrowUpFromLine, Loader2, CheckCircle2, XCircle, Inbox } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { formatNXC } from '../lib/formatNXC';

// iter233 — Cash Requests hub. Members see their own cash-in / cash-out
// requests; a group's cash holder (or super admin) also sees + confirms
// pending requests for the groups they hold.
const STATUS_STYLES = {
  pending: 'bg-yellow-500/15 text-yellow-700 border-yellow-500/40',
  confirmed: 'bg-green-500/15 text-green-700 border-green-500/40',
  cancelled: 'bg-muted text-muted-foreground border-border',
};

function RequestCard({ req, kind, user, busyId, onConfirm, onCancel }) {
  const requesterId = kind === 'in' ? req.payer_id : req.payee_id;
  const requesterName = kind === 'in' ? req.payer_name : req.payee_name;
  const isMine = requesterId === user?.id;
  const pending = req.status === 'pending';
  const busy = busyId === req.id;
  return (
    <Card className="p-3 border-2 rounded-2xl" data-testid={`cash-request-${req.id}`}>
      <div className="flex items-start gap-3">
        <div className={`p-2 rounded-xl shrink-0 ${kind === 'in' ? 'bg-green-500/10' : 'bg-orange-500/10'}`}>
          {kind === 'in'
            ? <ArrowDownToLine className="w-4 h-4 text-green-600" />
            : <ArrowUpFromLine className="w-4 h-4 text-orange-600" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-bold">{formatNXC(req.amount)}</p>
            <Badge variant="outline" className={`text-xs uppercase ${STATUS_STYLES[req.status] || ''}`}>
              {req.status}
            </Badge>
            <Badge variant="outline" className="text-xs uppercase">{req.method}</Badge>
          </div>
          <p className="text-sm text-muted-foreground truncate">
            {isMine ? 'You' : requesterName} · {req.group_name}
            {kind === 'in' ? ' · cash in' : ' · cash out'}
          </p>
          {req.note && <p className="text-xs text-muted-foreground italic truncate">"{req.note}"</p>}
          <p className="text-xs text-muted-foreground mt-0.5">
            {new Date(req.created_at).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
          </p>
        </div>
      </div>
      {pending && (
        <div className="flex gap-2 mt-3">
          {!isMine || user?.is_super_admin ? (
            <Button
              size="sm"
              className="flex-1 rounded-full font-bold"
              disabled={busy}
              onClick={() => onConfirm(req)}
              data-testid={`confirm-request-${req.id}`}
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
              {kind === 'in' ? 'Cash received' : 'Cash handed over'}
            </Button>
          ) : null}
          <Button
            size="sm"
            variant="outline"
            className="flex-1 rounded-full"
            disabled={busy}
            onClick={() => onCancel(req)}
            data-testid={`cancel-request-${req.id}`}
          >
            <XCircle className="w-4 h-4 mr-1" /> Cancel
          </Button>
        </div>
      )}
    </Card>
  );
}

export default function CashRequestsPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [cashIns, setCashIns] = useState([]);
  const [cashOuts, setCashOuts] = useState([]);
  const [busyId, setBusyId] = useState(null);

  const fetchAll = useCallback(async () => {
    try {
      const [ci, co] = await Promise.all([
        api.get('/contribution-requests'),
        api.get('/cash-out-requests'),
      ]);
      setCashIns(ci.data?.requests || []);
      setCashOuts(co.data?.requests || []);
    } catch {
      toast.error('Failed to load cash requests');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const act = async (path, req, okMsg) => {
    setBusyId(req.id);
    try {
      await api.post(path);
      toast.success(okMsg);
      await fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Action failed');
    } finally {
      setBusyId(null);
    }
  };

  const pendingCount = [...cashIns, ...cashOuts].filter((r) => r.status === 'pending').length;

  return (
    <div className="p-4 sm:p-6 max-w-2xl lg:max-w-none mx-auto space-y-5" data-testid="cash-requests-page">
      <div>
        <h1 className="font-display text-2xl font-bold flex items-center gap-2">
          <HandCoins className="w-6 h-6 text-primary" /> Cash Requests
        </h1>
        <p className="text-muted-foreground text-sm mt-0.5">
          Cash in / cash out with your group's cash holder.
          {pendingCount > 0 && ` ${pendingCount} pending.`}
        </p>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
      ) : cashIns.length === 0 && cashOuts.length === 0 ? (
        <Card className="p-8 border-2 rounded-2xl text-center" data-testid="cash-requests-empty">
          <Inbox className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
          <p className="font-semibold">No cash requests yet</p>
          <p className="text-sm text-muted-foreground mt-1">
            Raise a cash-in from your group's Fund tab, or a cash-out from your balance when the group owes you.
          </p>
        </Card>
      ) : (
        <>
          {cashIns.length > 0 && (
            <div className="space-y-2">
              <h2 className="font-display font-bold text-sm uppercase tracking-wide text-muted-foreground">
                Cash In (contributions)
              </h2>
              {cashIns.map((r) => (
                <RequestCard
                  key={r.id} req={r} kind="in" user={user} busyId={busyId}
                  onConfirm={(req) => act(`/contribution-requests/${req.id}/confirm`, req, 'Contribution confirmed — coins credited')}
                  onCancel={(req) => act(`/contribution-requests/${req.id}/cancel`, req, 'Request cancelled')}
                />
              ))}
            </div>
          )}
          {cashOuts.length > 0 && (
            <div className="space-y-2">
              <h2 className="font-display font-bold text-sm uppercase tracking-wide text-muted-foreground">
                Cash Out (withdrawals)
              </h2>
              {cashOuts.map((r) => (
                <RequestCard
                  key={r.id} req={r} kind="out" user={user} busyId={busyId}
                  onConfirm={(req) => act(`/cash-out-requests/${req.id}/confirm`, req, 'Cash-out confirmed — coins deducted')}
                  onCancel={(req) => act(`/cash-out-requests/${req.id}/cancel`, req, 'Request cancelled')}
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
